import React from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, Stack, Button, TextField } from '@mui/material';
import { Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { theme } from '../../../theme';
import TextingConfirmationDialog from '../../Campaign/CampaignTexting/TextingConfirmationDialog';
import TextingPreviewSummary from '../../Campaign/CampaignTexting/TextingPreviewSummary';
import { Grid } from '@mui/material';
import { useCreateBulkTextMessageMutation, useGetPhysicianTextingInfoQuery } from '../../../api/textingSlice';

function BulkTextingDialog({ open, onClose, physicianIds = [] }) {
    const [textType, setTextType] = useState('none');
    const [includeLink, setIncludeLink] = useState(false);
    const [message, setMessage] = useState('');
    const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
    const [createBulkTextMessage] = useCreateBulkTextMessageMutation();
    const { data: physicians = [] } = useGetPhysicianTextingInfoQuery(physicianIds, { skip: physicianIds.length === 0 });

    // normalize API response to an array (some endpoints return an object map)
    const physicianList = useMemo(() => {
        if (Array.isArray(physicians)) return physicians;
        if (!physicians) return [];
        return Object.values(physicians);
    }, [physicians]);
    const [selectedPhysicianIds, setSelectedPhysicianIds] = useState([]);

    // initialize selection based on message type and fetched physicians
    useEffect(() => {
        if (!physicianList || physicianList.length === 0) {
            setSelectedPhysicianIds([]);
            return;
        }

        const isKickoffText = textType === 'kickoff';
        const isFollowUpText = textType === 'follow-up';

        const projected = physicianList.filter((physician) => {
            if (isKickoffText) return !physician.do_not_text && physician.phone_number && !physician.was_recently_texted;
            if (isFollowUpText) return !physician.do_not_text && physician.phone_number && physician.has_reviewed_any_current_drafts && !physician.was_recently_texted;
            return false;
        }).map((p) => p.id);

        setSelectedPhysicianIds(projected);
    }, [physicians, textType]);

    function handleMessageTypeChange(value) {
        if (value === 'kickoff') {
            setIncludeLink(true);
            setSelectedPhysicianIds(physicianList.filter((physician) => !physician.do_not_text && physician.phone_number && !physician.was_recently_texted).map((p) => p.id));
        } else if (value === 'follow-up') {
            setSelectedPhysicianIds(physicianList.filter((physician) => !physician.do_not_text && physician.phone_number && physician.has_reviewed_any_current_drafts && !physician.was_recently_texted).map((p) => p.id));
        }
        setTextType(value);
    }

    function handleAddVariable(type) {
        if (type === 'surgeon_name') {
            setMessage(message + '[[surgeon_last_name]]');
        }
        if (type === 'savings') {
            setMessage(message + '[[savings_opportunity]]');
        }
    }

    async function handleSendTextMessage() {
        await createBulkTextMessage({
            messageType: textType,
            messageText: message,
            includeLink: includeLink,
            physicianIds: selectedPhysicianIds,
        });
        setIsShowingConfirmation(false);
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Send Bulk Text Message</DialogTitle>
            <DialogContent>
                <Grid container>
                    <Grid item xs={6}>
                        <Stack spacing={4}>
                            <Stack spacing={1}>
                                <Typography variant='normal' fontSize='16px'>Select a type of text message to send:</Typography>
                                <Select variant='outlined' value={textType} onChange={(e) => handleMessageTypeChange(e.target.value)}>
                                    <MenuItem value='none'>Select a message template</MenuItem>
                                    <MenuItem value='kickoff'>Kickoff Template</MenuItem>
                                    <MenuItem value='follow-up'>Standard Follow-Up Template</MenuItem>
                                    <MenuItem value='custom'>Custom Message</MenuItem>
                                </Select>
                            </Stack>
                            <Stack spacing={1}>
                                <TextField
                                    label='Message'
                                    multiline
                                    minRows={4}
                                    variant='outlined'
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    fullWidth
                                />
                                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                    <FormControlLabel 
                                        control={<Checkbox checked={includeLink} disabled={textType === 'kickoff'} onChange={() => setIncludeLink(!includeLink)} />} 
                                        label={
                                            <Stack>
                                                <Typography variant='normal' fontSize='14px'>Include physician dashboard link?</Typography>
                                                { textType === 'kickoff' && 
                                                    <Typography variant='normal' fontSize='12px' color={theme.palette.red.main}>Links will always be included in kickoff texts.</Typography>
                                                }
                                            </Stack>
                                        }
                                        name='link'
                                    />
                                    <Stack direction='row' spacing={2}>
                                        <Button variant='contained' color='primary' onClick={() => handleAddVariable('surgeon_name')}>Add Surgeon Last Name Variable</Button>
                                        <Button variant='contained' color='primary' onClick={() => handleAddVariable('savings')}>Add Savings Opportunity Variable</Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Button variant='contained' color='primary' disabled={textType === 'none' || message === ''} onClick={() => setIsShowingConfirmation(true)}>Send Text</Button>
                            <TextingConfirmationDialog open={isShowingConfirmation} onCancel={() => setIsShowingConfirmation(false)} onConfirm={handleSendTextMessage} />
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <TextingPreviewSummary
                            allPhysicians={physicians}
                            selectedIds={selectedPhysicianIds}
                            onSelectionChange={(nextIds) => setSelectedPhysicianIds(nextIds)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

export default BulkTextingDialog;
